/**
 * SpecialistID — Product PDP modular bundle: UI / PRODUCT
 * Part of the custom-product-* set (split out of the legacy custom-product.js).
 *
 * Owns all product-page initialisation: volume-pricing toggle, primary button,
 * discontinued-product check, error-message observer, swatch click delegation,
 * quantity input handlers, set-qty-max link, support popup, FPD designer help,
 * image popup, thumbnail slider, feedback, address management buttons, and the
 * initial optionLogic / mapMsrpLogic / manageStockDisplay calls on load.
 *
 * Public API: SPID.initOnReady()
 *
 * LOAD ORDER: after all custom-product-*.js modules, before custom-product.js.
 */
(function () {
  'use strict';

  const SPID = window.SPID;
  const CONFIG = SPID.config;
  const setAddToCartOutOfStock = SPID.setAddToCartOutOfStock;
  const loadProductData = SPID.loadProductData;
  const getVariantId = SPID.getVariantId;
  const getVariantData = SPID.getVariantData;
  const getVariantPrice = SPID.getVariantPrice;
  const getVariantMetafield = SPID.getVariantMetafield;
  const getProductMetafield = SPID.getProductMetafield;
  const mapMsrpLogic = SPID.mapMsrpLogic;
  const manageStockDisplay = SPID.optional('manageStockDisplay');
  const quantityLogic = SPID.optional('quantityLogic');
  const doneTyping = SPID.doneTyping;
  const updateTablePrice = SPID.optional('updateTablePrice');
  const optionLogic = SPID.optional('optionLogic');

  function initOnReady() {
    // Load product data from JSON
    loadProductData();

    // §3.4.1 Custom/FPD PDP: tag <body> so the custom-only CSS applies, and
    // start the discount table collapsed on mobile. #fpd / #supportPopup exist
    // only on the custom/FPD product templates.
    const isCustomPdp = !!(document.getElementById("fpd") || document.getElementById("supportPopup"));
    if (isCustomPdp) document.body.classList.add("custom-product-page");

    const volPricingTableTarget = document.querySelector("#regios-dopp-volume-pricing-table-target");
    if (volPricingTableTarget && volPricingTableTarget.parentElement) {
      const vpWrapper = volPricingTableTarget.parentElement;
      vpWrapper.classList.add("volume-pricing-table-wrapper", "open");
      // Mobile custom/FPD pages: start the discount table collapsed.
      if (isCustomPdp && window.matchMedia("(max-width: 749px)").matches) {
        vpWrapper.classList.remove("open");
      }
    }

    // Volume pricing click handlers
    document.querySelectorAll(".volume_pricing_message, #discount-progress, .volume_pricing_info").forEach(el => {
      el.addEventListener('click', function() {
        const wrapper = document.querySelector(".volume-pricing-table-wrapper");
        if (wrapper) wrapper.classList.toggle("open");
      });
    });

    const bulkMessage = document.querySelector(".bulk-ordering-message");
    const wrapper = document.querySelector(".volume-pricing-table-wrapper");
    if (bulkMessage && wrapper) {
      wrapper.appendChild(bulkMessage);
    }

    // §3.3 Mobile custom/FPD pages: move the "Bulk Ordering? Complex Project?"
    // link out of the volume-pricing area to sit directly below the Standard
    // Product / Get a Quote buttons. Desktop keeps it in the volume wrapper.
    if (bulkMessage && (document.getElementById("fpd") || document.getElementById("supportPopup"))) {
      const quoteWrapper = document.querySelector(".customize_quote_buttons_wrapper");
      if (quoteWrapper) {
        const bulkMq = window.matchMedia("(max-width: 749px)");
        const placeBulkMessage = function () {
          if (bulkMq.matches) {
            quoteWrapper.insertAdjacentElement("afterend", bulkMessage);
            bulkMessage.classList.add("bulk-moved-below");
          } else {
            if (wrapper) wrapper.appendChild(bulkMessage);
            bulkMessage.classList.remove("bulk-moved-below");
          }
        };
        placeBulkMessage();
        if (bulkMq.addEventListener) bulkMq.addEventListener("change", placeBulkMessage);
        else if (bulkMq.addListener) bulkMq.addListener(placeBulkMessage);
      }
    }

    // Primary button click handler
    document.querySelectorAll("button.button--primary").forEach(btn => {
      btn.addEventListener('click', function() {
        const spinner = document.querySelector(".loading-overlay__spinner");
        if (spinner) spinner.classList.remove("hidden");
        const secondaryBtn = document.querySelector("button.button--secondary");
        if (secondaryBtn) secondaryBtn.click();
        setTimeout(function() {
          if (spinner) spinner.classList.add("hidden");
        }, CONFIG.ANIMATION_DELAY);
      });
    });

    // Initial page load setup
    setTimeout(function() {
      document.body.classList.add('loaded');

      // Ensure product data is loaded
      loadProductData().then(() => {
        updateTablePrice();

        const isDiscontinued = SPID.state.productData?.metafields?.status?.discontinued ||
                              SPID.state.productData?.metafields?.c_f?.discontinued ||
                              document.querySelector('[name="isDiscontinued"]')?.value;
        if (isDiscontinued && isDiscontinued !== "false") {
          // Add discontinued class to body for CSS handling
          document.body.classList.add('discontinued');

          const outOfStockBtn = document.querySelector("a.product-form__submit-out-of-stock");
          if (outOfStockBtn) outOfStockBtn.classList.add("discontinued");

          // Disable add to cart button for discontinued products
          const addToCartBtn = document.querySelector("button.product-form__submit");
          if (addToCartBtn) {
            addToCartBtn.disabled = true;
            addToCartBtn.setAttribute('aria-disabled', 'true');
          }
        }
      });
    }, CONFIG.ANIMATION_DELAY);

    // Error message monitoring
    const errorMsg = document.querySelector(".product-form__error-message");
    if (errorMsg) {
      const observer = new MutationObserver(function() {
        var text = errorMsg.innerHTML;
        var variant_id = getVariantId();
        var colorSwatchEl = document.querySelector("div.color-swatch-wrapper.option-color");
        var variantName = '';
        if (colorSwatchEl) {
          const swatchItem = colorSwatchEl.querySelector(`[data-id='${variant_id}']`);
          if (swatchItem) variantName = swatchItem.dataset.option || '';
        }
        var titleEl = document.querySelector(".product__title h1");
        var title = titleEl ? titleEl.innerHTML : '';
        var generatedMessage = `All 0 ${title.trim()}${variant_id ? ` - ${variantName}` : ''} are in your cart.`;

        if (text === generatedMessage) {
          errorMsg.innerHTML = 'Sorry, this item is currently out of stock, please contact us...';
        }
      });

      observer.observe(errorMsg, { childList: true, characterData: true, subtree: true });
    }

    // Remove h1 elements from .custom-info-block > .rte
    const rteBlock = document.querySelector(".custom-info-block > .rte");
    if (rteBlock) {
      const h1Elements = rteBlock.querySelectorAll("h1");
      h1Elements.forEach(h1 => h1.remove());
    }

    // Animated background setup
    setTimeout(function() {
      const bg = document.querySelector(".animated-background");
      if (bg) bg.classList.remove('hidden');
    }, 300);

    // Color swatch and option clicks (using event delegation for dynamic elements)
    document.addEventListener('click', function(e) {
      const label = e.target.closest("label.color-swatch, label.other-option, label.swatch-input__label");
      if (!label) return;

      const bg = document.querySelector(".animated-background");
      if (bg) bg.classList.remove('hidden');
      var variant_id = label.dataset.id;

      setAddToCartOutOfStock(false);

      // Ensure product data is loaded before processing
      loadProductData().then(() => {
        optionLogic(variant_id);
        setTimeout(() => updateTablePrice(), CONFIG.ANIMATION_DELAY);
      });
    });

    // Out of stock click
    document.querySelectorAll("a.out-of-stock").forEach(link => {
      link.addEventListener('click', function() {
        var variant_id = getVariantId();
        if (variant_id !== undefined) {
          quantityLogic();
        }
      });
    });

    // Number input validation
    document.addEventListener('keydown', handleNumberInput);
    document.addEventListener('keyup', handleNumberInput);
    document.addEventListener('change', handleNumberInput);

    function handleNumberInput(e) {
      if (e.target.id === 'facility_code' || e.target.id === 'starting_number') {
        var max = parseInt(e.target.getAttribute('max'));
        var min = parseInt(e.target.getAttribute('min'));
        if (e.target.value > max) {
          e.target.value = max;
        } else if (e.target.value < min) {
          e.target.value = min;
        }
      }
    }

    // Quantity input change
    document.addEventListener('change', function(e) {
      if (e.target.classList.contains('quantity__input')) {
        doneTyping();
      }
    });

    // Quantity input keydown
    document.addEventListener('keydown', function(e) {
      if (e.target.classList.contains('quantity__input') && e.which === 13) {
        doneTyping();
      }
    });

    // "Set quantity to N" link inside out-of-stock message
    document.addEventListener('click', function(e) {
      const link = e.target.closest('.set-qty-max');
      if (!link) return;
      e.preventDefault();
      const qtyInput = document.querySelector('.quantity__input');
      if (!qtyInput) return;
      const newQty = parseInt(link.getAttribute('data-qty')) || 1;
      // This is the remaining-stock quantity for a deny variant, which may be
      // below the pack increment. Allow it through without rounding up.
      SPID.state.allowExactQty = true;
      qtyInput.value = newQty;
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      qtyInput.focus();
    });

    // Color swatch value update
    document.querySelectorAll(".color-swatch-wrapper .color-swatch-item").forEach(el => {
      el.addEventListener('click', function() {
        const labelEl = document.querySelector(".form__label .option-label");
        if (labelEl) labelEl.innerHTML = this.value;
        var variant_id = this.dataset.id;
        // Price is now handled by optionLogic
      });
    });

    // Address management
    document.querySelectorAll(".customer.addresses button.edit_button").forEach(btn => {
      btn.addEventListener('click', function() {
        const isExpanded = this.getAttribute('aria-expanded') === 'true';
        this.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
      });
    });

    document.querySelectorAll(".customer.addresses button.cancel_button").forEach(btn => {
      btn.addEventListener('click', function() {
        const editBtn = document.querySelector(".customer.addresses button.edit_button");
        if (editBtn) editBtn.setAttribute('aria-expanded', 'false');
      });
    });

    document.querySelectorAll(".add_address, .add_address_cancel").forEach(el => {
      el.addEventListener('click', function() {
        const wrapper = document.querySelector(".add-address-wrapper");
        if (wrapper) wrapper.style.display = wrapper.style.display === 'none' ? 'block' : 'none';
      });
    });

    document.querySelectorAll("form.address-delete-form").forEach(form => {
      form.addEventListener('submit', function(event) {
        const deleteBtn = document.querySelector("button.delete_button");
        const msg = deleteBtn ? deleteBtn.dataset.confirmMessage : '';
        var confirmation = confirm(msg);
        if (!confirmation) {
          event.preventDefault();
        }
      });
    });

    // Image popup functionality
    document.querySelectorAll('.product-thumbnails-popup img').forEach(img => {
      img.addEventListener('click', function() {
        const fullImageSrc = this.dataset.fullImage;
        const popup = document.querySelector('#image-popup');
        const popupImg = document.querySelector('#popup-img');
        if (popupImg) popupImg.src = fullImageSrc;
        if (popup) popup.style.display = 'block';
      });
    });

    const closePopup = document.querySelector('#close-popup');
    const imagePopup = document.querySelector('#image-popup');
    if (closePopup) {
      closePopup.addEventListener('click', function() {
        if (imagePopup) imagePopup.style.display = 'none';
      });
    }

    if (imagePopup) {
      imagePopup.addEventListener('click', function() {
        this.style.display = 'none';
      });
    }

    const popupContent = document.querySelector('.popup-content');
    if (popupContent) {
      popupContent.addEventListener('click', function(event) {
        event.stopPropagation();
      });
    }

    // Thumbnail slider
    document.querySelectorAll('.product-thumbnails li img').forEach(img => {
      img.addEventListener('click', function() {
        const newImageSrc = this.dataset.fullImage;
        const newImageAlt = this.getAttribute('alt');
        const mainImg = document.querySelector('.product-main-image img');
        if (mainImg) {
          mainImg.src = newImageSrc;
          mainImg.alt = newImageAlt;
        }
      });
    });

    // Thumbnail navigation
    var visibleThumbs = 4;
    var totalThumbs = document.querySelectorAll('.product-thumbnails li').length;
    var maxIndex = totalThumbs - visibleThumbs;
    var currentIndex = 0;

    document.querySelectorAll('.thumbnail-slider .next-btn').forEach(btn => {
      btn.addEventListener('click', function() {
        if (currentIndex < maxIndex) {
          currentIndex++;
          updateThumbnailPosition();
        }
      });
    });

    document.querySelectorAll('.thumbnail-slider .prev-btn').forEach(btn => {
      btn.addEventListener('click', function() {
        if (currentIndex > 0) {
          currentIndex--;
          updateThumbnailPosition();
        }
      });
    });

    function updateThumbnailPosition() {
      const thumbs = document.querySelector('.product-thumbnails li');
      const thumbWidth = thumbs ? thumbs.offsetWidth + 10 : 0;
      const moveDistance = currentIndex * (thumbWidth * -1);
      const thumbContainer = document.querySelector('.product-thumbnails');
      if (thumbContainer) thumbContainer.style.transform = 'translateX(' + moveDistance + 'px)';
    }

    // Tooltip functionality has been moved to custom-tooltip.js

    // Initialize pricing and stock
    setTimeout(() => {
      loadProductData().then(() => {
        mapMsrpLogic();
        manageStockDisplay();

        // Initialize minimum quantity from metafields
        const initialVariantId = getVariantId();
        if (initialVariantId) {
          optionLogic(initialVariantId);
        }
      });
    }, 100);

    // Sliding support popup
    setTimeout(function() {
      const popup = document.querySelector("#supportPopup");
      const openBtn = document.querySelector("#openPopup");
      if (popup) popup.style.left = "10px";
      setTimeout(function() {
        if (popup) popup.style.left = "-300px";
        if (openBtn) openBtn.style.display = 'block';
      }, CONFIG.POPUP_HIDE_DELAY);
    }, CONFIG.POPUP_DELAY);

    const closePopupBtn = document.querySelector("#closePopup");
    if (closePopupBtn) {
      closePopupBtn.addEventListener('click', function() {
        const popup = document.querySelector("#supportPopup");
        const openBtn = document.querySelector("#openPopup");
        if (popup) popup.style.left = "-300px";
        if (openBtn) openBtn.style.display = 'block';
      });
    }

    const openPopupBtn = document.querySelector("#openPopup");
    if (openPopupBtn) {
      openPopupBtn.addEventListener('click', function() {
        const popup = document.querySelector("#supportPopup");
        if (popup) popup.style.left = "10px";
        this.style.display = 'none';
      });
    }

    // FPD designer help popup
    document.querySelectorAll(".support-content-wrapper, .close-designer-help-form").forEach(el => {
      el.addEventListener('click', function() {
        const overlay = document.querySelector("#overlay-support");
        const helpPopup = document.querySelector("#fpd-designer-help-popup");
        if (overlay) overlay.classList.toggle('hidden');
        if (helpPopup) helpPopup.classList.toggle('hidden');
      });
    });

    // Feedback functionality
    document.querySelectorAll('.feedback-container > .positive').forEach(el => {
      el.addEventListener('click', function() {
        const positiveFeedback = document.querySelector(".positive-feedback");
        if (positiveFeedback) {
          positiveFeedback.classList.remove("hide");
          setTimeout(function() {
            positiveFeedback.classList.add("hide");
          }, 5000);
        }
      });
    });

    document.querySelectorAll('.feedback-container > .negative').forEach(el => {
      el.addEventListener('click', function() {
        const feedbackForm = document.querySelector("#feedback_form");
        if (feedbackForm) {
          const iframe = feedbackForm.querySelector('iframe');
          if (iframe) {
            const productUrl = feedbackForm.dataset.productUrl;
            if (productUrl && !iframe.src.includes('Field3=')) {
              const sep = iframe.src.includes('?') ? '&' : '?';
              iframe.src = iframe.src + sep + 'Field3=' + encodeURIComponent(productUrl);
            }
          }
          feedbackForm.classList.remove("hide");
        }
      });
    });

    document.querySelectorAll('#feedback_form a.close').forEach(el => {
      el.addEventListener('click', function() {
        const feedbackForm = document.querySelector("#feedback_form");
        if (feedbackForm) feedbackForm.classList.add("hide");
      });
    });
  }

  SPID.initOnReady = initOnReady;

  // Announce this module so main-product.liquid can leave it out safely.
  if (window.SPID && SPID.register) SPID.register('ui-product');
})();
